import { FeedbackParam } from '../types/backendService';
import { TFunction } from 'react-i18next';
import { titleizeString } from './string_utils';
import moment from 'moment';

export interface FeedbackDocumentFields {
  summary: string;
  incidentDetails?: string;
  areasOfImprovement?: string;
}

const positiveFieldMapping: Record<string, Record<string, (feedback: FeedbackParam, t: TFunction) => FeedbackDocumentFields>> = {
  'DEFAULT': {
    'DEFAULT': (feedback: FeedbackParam, t: TFunction) => {
      const summary = t('generalPositiveSummary', {
        type: titleizeString(feedback.type!),
        interpolation: {'escapeValue': false},
      });

      return {
        summary,
        incidentDetails: feedback.incidentDetails,
        areasOfImprovement: feedback.areasOfImprovement,
      };
    },
    'APPRECIATION_FEEDBACK': (feedback: FeedbackParam, t: TFunction) => {
      const summary = t('appreciationPositiveSummary', {
        type: titleizeString(feedback.type!),
        interpolation: {'escapeValue': false},
      });

      return {
        summary,
        incidentDetails: feedback.incidentDetails,
        areasOfImprovement: feedback.areasOfImprovement,
      };
    }
  }
}

const regularFieldMapping: Record<string, Record<string, (feedback: FeedbackParam, t: TFunction) => FeedbackDocumentFields>> = {
  'ATTENDANCE': {
    'DEFAULT': (feedback: FeedbackParam, t: TFunction) => {
      let summary = '';
      let incidentDetails = '';
      
      if (feedback.details!.unpaidPersonalTimeHours == undefined) {
        summary = t('attendanceSummaryWithoutUptBalance');
      } else {
        summary = t('attendanceSummaryWithUptBalance');
      }

      if (feedback.details!.unpaidPersonalTimeHours == undefined
          && feedback.details!.attendancePullDateUtc != undefined
          && feedback.details!.attendancePoints != undefined) {
        incidentDetails = t('attendanceIncidentDetailsWithoutUptBalance', {
          currentBalanceDate: moment(feedback.details!.attendancePullDateUtc * 1000).format('M/D/Y'),
          attendancePoints: feedback.details!.attendancePoints,
          interpolation: {'escapeValue': false},
        });
      } else if (feedback.details!.unpaidPersonalTimeHours != undefined
          && feedback.details!.attendancePullDateUtc != undefined
          && feedback.details!.unpaidPersonalTimeHours != undefined) {
        incidentDetails = t('attendanceIncidentDetailsWithUptBalance', {
          currentBalanceDate: moment(feedback.details!.attendancePullDateUtc * 1000).format('M/D/Y'),
          uptCurrentBalance: feedback.details!.unpaidPersonalTimeHours,
          interpolation: {'escapeValue': false},
        });
      }

      return {
        summary,
        incidentDetails,
      };
    }
  },
  'SAFETY': {
    'DEFAULT': (feedback: FeedbackParam, t: TFunction) => {
      const summary = t('generalSummary', {
        type: 'Safety',
        interpolation: {'escapeValue': false},
      });

      return {
        summary,
        incidentDetails: feedback.incidentDetails,
        areasOfImprovement: feedback.areasOfImprovement,
      };
    }
  },
  'QUALITY': {
    'DEFAULT': (_feedback: FeedbackParam, t: TFunction) => {
      const summary = t('generalSummary', {
        type: 'Quality',
        interpolation: {'escapeValue': false},
      });

      return {
        summary,
        incidentDetails: t('qualityIncidentDetails'),
        areasOfImprovement: t('qualityAreasOfImprovement'),
      };
    }
  },
  'PRODUCTIVITY': {
    'DEFAULT': (feedback: FeedbackParam, t: TFunction) => {
      const summary = t('generalSummary', {
        type: 'Productivity',
        interpolation: {'escapeValue': false},
      });

      let dateRange = 'N/A';
      if (feedback.details!.dateRange!) {
        dateRange = moment(feedback.details!.dateRange! * 1000).utc().add(7, 'days').format('M/D/Y');
      }

      const incidentDetails = t('productivityIncidentDetails', {
        date: dateRange,
        interpolation: {'escapeValue': false},
      });

      return {
        summary,
        incidentDetails,
        areasOfImprovement: t('productivityAreasOfImprovement'),
      };
    }
  },
  'BEHAVIORAL_ATTENDANCE': {
    'DEFAULT': (feedback: FeedbackParam, t: TFunction) => {
      const summary = t('generalSummary', {
        type: 'Behavioral Attendance',
        interpolation: {'escapeValue': false},
      });

      return {
        summary,
        areasOfImprovement: feedback.areasOfImprovement,
      };
    }
  },
  'BEHAVIORAL': {
    'DEFAULT': (feedback: FeedbackParam, t: TFunction) => {
      const summary = t('generalSummary', {
        type: 'Behavioral',
        interpolation: {'escapeValue': false},
      });

      return {
        summary,
        incidentDetails: feedback.incidentDetails,
        areasOfImprovement: feedback.areasOfImprovement,
      };
    }
  },
  'PRODUCTIVITY_SINGLE_DAY': {
    'DEFAULT': (feedback: FeedbackParam, t: TFunction) => {
      const summary = t('generalSummary', {
        type: 'Productivity Single Day',
        interpolation: {'escapeValue': false},
      });

      return {
        summary,
        incidentDetails: feedback.incidentDetails,
        areasOfImprovement: feedback.areasOfImprovement,
      };
    }
  },
  'BEHAVIORAL_TIME_OFF_TASK': {
    'DEFAULT': (feedback: FeedbackParam, t: TFunction) => {
      const summary = t('generalSummary', {
        type: 'Idle Time',
        interpolation: {'escapeValue': false},
      });

      return {
        summary,
        incidentDetails: feedback.incidentDetails,
        areasOfImprovement: feedback.areasOfImprovement,
      };
    }
  },
  'BEHAVIORAL_IDLE_TIME': {
    'DEFAULT': (feedback: FeedbackParam, t: TFunction) => {
      const summary = t('generalSummary', {
        type: 'Idle Time',
        interpolation: {'escapeValue': false},
      });

      return {
        summary,
        areasOfImprovement: feedback.areasOfImprovement,
      };
    }
  },
  'JOB_PERFORMANCE': {
    'DEFAULT': (feedback: FeedbackParam, t: TFunction) => {
      const summary = t('generalSummary', {
        type: 'Productivity',
        interpolation: {'escapeValue': false},
      });

      return {
        summary,
        incidentDetails: feedback.comments,
        areasOfImprovement: feedback.areasOfImprovement,
      };
    }
  },
  'DEFAULT': {
    'DEFAULT': (feedback: FeedbackParam, t: TFunction) => {
      const summary = t('generalSummary', {
        type: titleizeString(feedback.type!),
        interpolation: {'escapeValue': false},
      });

      return {
        summary,
        incidentDetails: feedback.incidentDetails,
        areasOfImprovement: feedback.areasOfImprovement,
      };
    }
  },
};

export const generateFeedbackDocumentFields = (feedback: FeedbackParam, t: TFunction): FeedbackDocumentFields|undefined => {
  const fieldMapping = feedback.feedbackCategory === 'Positive' ? positiveFieldMapping : regularFieldMapping;
  const fieldMappingSubmap = fieldMapping[feedback.type!] || fieldMapping['DEFAULT'];

  return (fieldMappingSubmap[feedback.level!] || fieldMappingSubmap['DEFAULT'])(feedback, t);
}
